import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { contactsApi, registryApi, mydataError } from "@/api";
import { useLang } from "@/i18n";
import { Landmark } from "lucide-react";

const EMPTY = { name: "", type: "customer", afm: "", doy: "", address: "", city: "", postal_code: "", phone: "", email: "", payment_terms: 30, notes: "", activity: "" };

const Field = ({ label, children, error }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
    {error && <p className="text-xs text-rose-600" data-testid="field-error">{error}</p>}
  </div>
);

export const ContactDialog = ({ open, onOpenChange, contact, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setForm(contact ? { ...EMPTY, ...contact } : EMPTY); setErrors({}); }
  }, [open, contact]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const [looking, setLooking] = useState(false);

  const lookup = async () => {
    if (!/^\d{9}$/.test(form.afm.trim())) { setErrors({ afm: t("afm_invalid") }); return; }
    setLooking(true);
    try {
      const r = await registryApi.lookup(form.afm.trim());
      setForm((f) => ({ ...f, name: r.name || f.name, doy: r.doy || f.doy, address: r.address || f.address, city: r.city || f.city,
        postal_code: r.postal_code || f.postal_code, activity: r.activity || f.activity,
        notes: [f.notes, r.legal_form ? `${t("legal_form")}: ${r.legal_form}` : "", !r.active ? t("inactive_afm") : ""].filter(Boolean).join(" · ") }));
      setErrors({});
      toast.success(r.mock ? t("aade_lookup_mock") : t("aade_lookup_ok"));
    } catch (err) { toast.error(mydataError(err, t)); } finally { setLooking(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = t("required");
    if (form.afm && !/^[A-Z]{0,2}\d{9}$/.test(form.afm.trim())) errs.afm = t("afm_invalid");
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const payload = { ...form, payment_terms: Number(form.payment_terms) || 0 };
      delete payload.id; delete payload.created_at;
      const saved = contact ? await contactsApi.update(contact.id, payload) : await contactsApi.create(payload);
      toast.success(t("contact_saved"));
      onSaved(saved);
      onOpenChange(false);
    } catch { toast.error(t("error")); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{contact ? t("edit_contact") : t("new_contact")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Field label={t("name")} error={errors.name}>
              <Input value={form.name} onChange={set("name")} data-testid="contact-form-name-input" autoFocus />
            </Field>
          </div>
          <Field label={t("type")}>
            <Select value={form.type} onValueChange={set("type")}>
              <SelectTrigger data-testid="contact-form-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">{t("customer")}</SelectItem>
                <SelectItem value="supplier">{t("supplier")}</SelectItem>
                <SelectItem value="both">{t("both")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("payment_terms")}>
            <Input type="number" min="0" value={form.payment_terms} onChange={set("payment_terms")} data-testid="contact-form-terms-input" className="font-mono" />
          </Field>
          <Field label={t("vat_id")} error={errors.afm}>
            <div className="flex gap-2">
              <Input value={form.afm} onChange={set("afm")} data-testid="contact-form-afm-input" className="font-mono" placeholder="123456789" />
              <Button type="button" variant="outline" onClick={lookup} disabled={looking} className="shrink-0" data-testid="contact-form-aade-lookup-button" title={t("aade_lookup")}>
                <Landmark size={14} className="mr-1" />{t("aade_lookup")}
              </Button>
            </div>
          </Field>
          <Field label={t("tax_office")}>
            <Input value={form.doy} onChange={set("doy")} data-testid="contact-form-doy-input" />
          </Field>
          <Field label={t("address")}>
            <Input value={form.address} onChange={set("address")} data-testid="contact-form-address-input" />
          </Field>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2"><Field label={t("city")}><Input value={form.city} onChange={set("city")} data-testid="contact-form-city-input" /></Field></div>
            <Field label={t("postal_code")}><Input value={form.postal_code} onChange={set("postal_code")} className="font-mono" data-testid="contact-form-postal-input" /></Field>
          </div>
          <div className="sm:col-span-2">
            <Field label={t("activity_field")}>
              <Input value={form.activity} onChange={set("activity")} data-testid="contact-form-activity-input" />
            </Field>
          </div>
          <Field label={t("phone")}>
            <Input value={form.phone} onChange={set("phone")} data-testid="contact-form-phone-input" />
          </Field>
          <Field label={t("email")}>
            <Input type="email" value={form.email} onChange={set("email")} data-testid="contact-form-email-input" />
          </Field>
          <div className="sm:col-span-2">
            <Field label={t("notes")}>
              <Textarea rows={2} value={form.notes} onChange={set("notes")} data-testid="contact-form-notes-input" />
            </Field>
          </div>
          <DialogFooter className="sm:col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="contact-form-cancel-button">{t("cancel")}</Button>
            <Button type="submit" disabled={saving} className="bg-blue-800 hover:bg-blue-700" data-testid="contact-form-submit-button">{t("save")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
